import axios from "@/utils/axios";
import { useToast } from "@elaniintc/goes-design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

type LimitUnit = "minute" | "hour" | "day" | "week" | "month" | "year";

export interface NodeExpirationBody {
  /** Display name (<=255) */
  display_name?: string;
  /** Available days (e.g. ['monday','tuesday',...]) */
  available_days?: string[];
  /** Limit unit: minute | hour | day | month | year */
  limit_unit: LimitUnit;
  /** Limit amount (>= 0) */
  limit_amount: number;
  /** Related node UUID (optional if the association already exists in the backend) */
  node_id?: string;
}

export interface UpdateNodeExpirationPayload {
  id: string;
  data: Partial<NodeExpirationBody>;
}

export const useGetNodeExpiration = (id: string) => {
  return useQuery({
    queryKey: ["admin-node-expiration", id],
    queryFn: async () => {
      const res = await axios.get(`/admin/v1/node-expiration/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
};

export const useCreateNodeExpiration = (nodeId?: string) => {
  const query = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (payload: { expiration: NodeExpirationBody }) => {
      const res = await axios.post("/admin/v1/node-expiration", {
        ...payload.expiration,
      });
      return res.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-node-expiration"] });
      if (nodeId) {
        query.invalidateQueries({ queryKey: ["admin-flow-detail", nodeId] });
      }
      toast.showToast({
        title: "Temporizador creado",
        description: "El temporizador del nodo se ha creado correctamente.",
        options: { type: "success", duration: 5000 },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al crear el temporizador",
        description: "Ocurrió un error al intentar crear el temporizador.",
        options: { type: "error", duration: 5000 },
      });
    },
  });
};

export const useUpdateNodeExpiration = (flowId?: string) => {
  const query = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: UpdateNodeExpirationPayload) => {
      const res = await axios.patch(`/admin/v1/node-expiration/${id}`, {
        ...data,
      });
      return res.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-node-expiration"] });
      if (flowId) {
        query.invalidateQueries({ queryKey: ["admin-flow-detail", flowId] });
      }
      toast.showToast({
        title: "Temporizador actualizado",
        description:
          "El temporizador del nodo se ha actualizado correctamente.",
        options: { type: "success", duration: 5000 },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al actualizar el temporizador",
        description: "Ocurrió un error al intentar actualizar el temporizador.",
        options: { type: "error", duration: 5000 },
      });
    },
  });
};

export const useDeleteNodeExpiration = (flowId?: string) => {
  const query = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.delete(`/admin/v1/node-expiration/${id}`);
      return res.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-node-expiration"] });
      if (flowId) {
        query.invalidateQueries({ queryKey: ["admin-flow-detail", flowId] });
      }
      toast.showToast({
        title: "Temporizador eliminado",
        description: "El temporizador del nodo se ha eliminado correctamente.",
        options: { type: "success", duration: 5000 },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al eliminar el temporizador",
        description: "Ocurrió un error al intentar eliminar el temporizador.",
        options: { type: "error", duration: 5000 },
      });
    },
  });
};

export const useCreateNodeTimeExtension = () => {
  return useMutation({
    mutationFn: async (data: {
      node_expiration_id: string;
      availableDays: string[];
      leaderRequired: boolean;
      extensionUnit: "minute" | "hour" | "day" | "week" | "month" | "year";
      extensionAmount: number;
    }) => {
      const response = await axios.post("/admin/v1/node-time-extensions", {
        node_expiration_id: data.node_expiration_id,
        available_days: data.availableDays,
        leader_required: data.leaderRequired,
        extension_unit: data.extensionUnit,
        extension_amount: data.extensionAmount,
        display_name: `Prórroga`,
        description: `Prórroga`,
      });
      return response.data;
    },
  });
};

export const useUpdateNodeTimeExtension = () => {
  return useMutation({
    mutationFn: async (data: {
      id: string;
      availableDays: string[];
      leaderRequired: boolean;
      extensionUnit: "minute" | "hour" | "day" | "week" | "month" | "year";
      extensionAmount: number;
    }) => {
      const response = await axios.patch(
        `/admin/v1/node-time-extensions/${data.id}`,
        {
          available_days: data.availableDays,
          leader_required: data.leaderRequired,
          extension_unit: data.extensionUnit,
          extension_amount: data.extensionAmount,
        },
      );
      return response.data;
    },
  });
};

export const useGetNodeTimeExtension = (id: string) => {
  return useQuery({
    queryKey: ["admin-node-time-extension", id],
    queryFn: async () => {
      const response = await axios.get(`/admin/v1/node-time-extensions/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useDeleteNodeTimeExtension = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/admin/v1/node-time-extensions/${id}`,
      );
      return response.data;
    },
  });
};
